/**
 * @module useFileUploadProcessor
 *
 * React hook for processing uploaded Excel/CSV files with multi-sheet support,
 * column normalization, validation, and optional merge with existing grid data.
 *
 * **Architecture role:** This is the mid-level hook in the upload pipeline. It
 * handles the full processing lifecycle: read file -> parse sheets -> normalize
 * headers -> apply transforms -> validate rows -> merge with existing data.
 *
 * **When to use this vs. `useUploadConfig`:**
 * - Use `useUploadConfig` (recommended) when your app has a `fieldDefs` JSON file
 *   or simple alias-based header mappings. It auto-generates the normalizer,
 *   transforms, and validators and delegates to this hook internally.
 * - Use `useFileUploadProcessor` directly when you need full control over sheet
 *   processing (e.g., multi-sheet workbooks with different processors per sheet,
 *   custom header row positions, or processing logic that does not fit the
 *   fieldDefs pattern).
 *
 * **Merge integration:** When `mergeStrategy` and `existingRows` are both
 * provided, the hook automatically calls `mergeRows()` from `mergeEngine.js`
 * after processing. The merge result is available via `lastMergeResult` and
 * is also attached to the `results.mergeResult` in the `onComplete` callback.
 *
 * @example <caption>Basic single-sheet processing</caption>
 * import { useFileUploadProcessor, createSheetProcessor } from '@1823-partners/core';
 *
 * const processor = createSheetProcessor({
 *   columnMappings: { 'cusip': 'CUSIP', 'book yield': 'BOOK_YIELD' },
 *   fieldTransforms: { BOOK_YIELD: coerceNumeric },
 *   requiredFields: ['CUSIP'],
 * });
 *
 * const { processFile, isProcessing, errors } = useFileUploadProcessor({
 *   defaultSheetProcessor: processor,
 *   onComplete: (results) => setGridData(results.allData),
 * });
 *
 * @example <caption>Multi-sheet processing (pricing app)</caption>
 * const { processFile } = useFileUploadProcessor({
 *   sheetProcessors: {
 *     'Securities': createSheetProcessor({ requiredFields: ['CUSIP'] }),
 *     'Prices': createSheetProcessor({ requiredFields: ['CUSIP', 'PRICE'] }),
 *   },
 *   defaultSheetProcessor: createSheetProcessor({}),
 *   onComplete: (results) => {
 *     // results.sheets['Securities'].data = [...]
 *     // results.sheets['Prices'].data = [...]
 *   },
 * });
 *
 * @example <caption>With merge support (CRE upsert)</caption>
 * const { processFile, lastMergeResult } = useFileUploadProcessor({
 *   defaultSheetProcessor: processor,
 *   columnNormalizer: myNormalizer,
 *   existingRows: gridData,
 *   matchField: 'CUSIP',
 *   mergeStrategy: 'upsert',
 *   uploadableFields: ['LOAN_NAME', 'YIELD', 'MATURITY_DATE'],
 *   onNewRow: (row) => ({ ...row, MODIFIED_BY: userId }),
 *   onComplete: (results) => {
 *     setGridData(results.mergeResult.mergedData);
 *     showNotification(`${results.mergeResult.newCount} new, ${results.mergeResult.updatedCount} updated`);
 *   },
 * });
 */
import { useState, useCallback } from 'react';
import { mergeRows } from '../utils/mergeEngine';

/**
 * @typedef {Object} SheetProcessorConfig
 * @property {number} [headerRow=0] - Row index (0-based) containing column headers.
 *   Set to 1+ if your spreadsheet has title rows above the headers.
 * @property {number} [dataStartRow=1] - Row index (0-based) where data begins.
 *   Typically `headerRow + 1`. Set higher to skip sub-header or instruction rows.
 * @property {Object<string, string>} [columnMappings={}] - Map of normalized column
 *   names (lowercase-with-spaces) to target field names (UPPER_CASE). Used as a
 *   fallback when `columnNormalizer` is not provided. Prefer using
 *   `columnNormalizer` from `buildHeaderMappings` + `createColumnNormalizer`.
 * @property {Object<string, Function>} [fieldTransforms={}] - Map of field names
 *   to transform functions. Applied to each cell value after header normalization.
 *   Build with `buildFieldTransforms` from `fieldTransforms.js`.
 * @property {boolean} [skipEmptyRows=true] - When true, rows where every cell is
 *   empty/null/undefined are skipped.
 * @property {Array<string>} [requiredFields=[]] - Fields that must be non-empty.
 *   Rows missing these fields generate errors but are still included in output.
 */

/**
 * @typedef {Object} ProcessingResults
 * @property {string} fileName - Name of the uploaded file
 * @property {'csv'|'excel'} fileType - Detected file type
 * @property {Object<string, SheetResult>} sheets - Per-sheet results keyed by sheet name
 * @property {Array<Object>} allData - Combined data from all processed sheets
 * @property {Array<ProcessingError>} allErrors - Combined errors from all sheets
 * @property {ProcessingSummary} summary - Aggregate statistics
 * @property {import('../utils/mergeEngine').MergeResult} [mergeResult] - Present only when
 *   `mergeStrategy` and `existingRows` are both provided.
 */

/**
 * @typedef {Object} SheetResult
 * @property {string} sheetName - Name of the sheet
 * @property {Array<Object>} data - Processed row objects from this sheet
 * @property {Array<ProcessingError>} errors - Errors from this sheet
 * @property {number} skipped - Number of empty rows skipped
 * @property {number} totalRows - Total data rows (excluding header)
 */

/**
 * @typedef {Object} ProcessingError
 * @property {number} row - 1-based row number in the original file
 * @property {string} field - Field name that caused the error
 * @property {string} message - Human-readable error description
 * @property {'required'|'validation'|'system'} type - Error category
 * @property {string} [sheet] - Sheet name (added when errors are aggregated)
 */

/**
 * @typedef {Object} ProcessingSummary
 * @property {number} totalSheets - Number of sheets processed
 * @property {number} totalRows - Total data rows across all sheets
 * @property {number} totalErrors - Total errors across all sheets
 * @property {Array<string>} processedSheets - Names of sheets that were processed
 */

/**
 * Hook for processing uploaded Excel/CSV files.
 *
 * Returns reactive state (isProcessing, progress, errors) and a `processFile`
 * function that handles the complete upload pipeline.
 *
 * @param {Object} options - Configuration options
 * @param {Object<string, SheetProcessorConfig>} [options.sheetProcessors={}] - Map of
 *   sheet name to processor configuration. Use this for multi-sheet workbooks where
 *   different sheets need different column mappings or transforms.
 * @param {SheetProcessorConfig} [options.defaultSheetProcessor] - Default processor for
 *   sheets not found in `sheetProcessors`. Also used for single-sheet files (CSV).
 *   If neither `sheetProcessors[name]` nor `defaultSheetProcessor` matches, an empty
 *   config `{}` is used (headers taken as-is, no transforms).
 * @param {Function} [options.columnNormalizer] - Function to normalize column header
 *   strings to canonical field names. Created by `createColumnNormalizer(mappings)`.
 *   When provided, this takes precedence over `columnMappings` in sheet processors.
 * @param {Function} [options.validateRow] - Row validation function with signature
 *   `(row, index) => ValidationError | null`. Called for each row after transforms.
 *   Build with `combineValidators` or `buildValidatorsFromFieldDefs`.
 * @param {Function} [options.onProgress] - Progress callback: `(percent) => void`.
 *   Called with values from 0 to 100 during processing.
 * @param {Function} [options.onComplete] - Called when processing completes successfully.
 *   Signature: `(results: ProcessingResults) => void`. The `results.mergeResult`
 *   field is populated when merge is configured.
 * @param {Function} [options.onError] - Called on unrecoverable error (e.g., file read
 *   failure, xlsx not installed). Signature: `(error: Error) => void`.
 * @param {Array<Object>} [options.existingRows] - Current grid data. Required for merge.
 *   When provided along with `mergeStrategy`, the hook calls `mergeRows()` after
 *   processing and attaches the result.
 * @param {string} [options.matchField] - Field to match uploaded rows against existing
 *   rows (e.g., `'CUSIP'`). Required when `mergeStrategy` is `'upsert'`.
 * @param {'append'|'upsert'|'replace'} [options.mergeStrategy] - Merge strategy.
 *   See `mergeRows` documentation for detailed strategy descriptions.
 * @param {boolean} [options.deduplicateUpload] - Deduplicate uploaded rows by
 *   `matchField` before merging.
 * @param {Array<string>} [options.uploadableFields] - Restrict which fields can be
 *   overwritten during upsert merge.
 * @param {Function} [options.onNewRow] - Callback for new rows during merge.
 *   Signature: `(row, index) => enrichedRow`.
 * @param {Function} [options.onUpdateRow] - Callback for matched rows during merge.
 *   Signature: `(existingRow, uploadedRow) => mergedRow`. Replaces default merge.
 * @returns {Object} Processing state and handlers:
 *   - `processFile` {Function} - `(file: File, options?: { targetSheet?: string }) => Promise<ProcessingResults>`.
 *     Pass the browser File object. Optionally specify `targetSheet` to process only one sheet.
 *   - `isProcessing` {boolean} - True while a file is being processed.
 *   - `progress` {number} - Processing progress from 0 to 100.
 *   - `errors` {Array<ProcessingError>} - Errors from the last processing run.
 *   - `hasErrors` {boolean} - True if `errors.length > 0`.
 *   - `lastResult` {ProcessingResults|null} - Full results from the last processing run.
 *   - `lastMergeResult` {MergeResult|null} - Merge result from the last run (when merge is configured).
 *   - `reset` {Function} - Clears all state (processing, errors, results).
 */
export const useFileUploadProcessor = ({
  sheetProcessors = {},
  defaultSheetProcessor,
  columnNormalizer,
  validateRow,
  onProgress,
  onComplete,
  onError,
  existingRows,
  matchField,
  mergeStrategy,
  deduplicateUpload,
  uploadableFields,
  onNewRow,
  onUpdateRow,
} = {}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);
  const [lastResult, setLastResult] = useState(null);
  const [lastMergeResult, setLastMergeResult] = useState(null);

  /**
   * Updates progress and notifies callback
   * @private
   */
  const updateProgress = useCallback(
    (value) => {
      setProgress(value);
      onProgress?.(value);
    },
    [onProgress]
  );

  /**
   * Process a single sheet's data through the normalization, transform, and
   * validation pipeline.
   *
   * @param {string} sheetName - Name of the sheet being processed
   * @param {Array<Array<*>>} rawData - 2D array from xlsx sheet_to_json(header:1)
   * @param {SheetProcessorConfig} processor - Configuration for this sheet
   * @returns {Promise<SheetResult>} Processed sheet data and errors
   * @private
   */
  const processSheet = useCallback(
    async (sheetName, rawData, processor) => {
      if (!rawData || rawData.length === 0) {
        return { sheetName, data: [], errors: [], skipped: 0 };
      }

      const {
        headerRow = 0,
        dataStartRow = 1,
        columnMappings = {},
        fieldTransforms = {},
        skipEmptyRows = true,
        requiredFields = [],
      } = processor;

      // Extract headers
      const headers = rawData[headerRow] || [];

      // Normalize headers
      const normalizedHeaders = headers.map((header) => {
        if (columnNormalizer) {
          return columnNormalizer(header);
        }
        // Use column mappings directly if no normalizer
        const normalized = header?.toString().toLowerCase().trim().replace(/[_\s-]+/g, ' ');
        return columnMappings[normalized] || header;
      });

      const processedData = [];
      const sheetErrors = [];
      let skippedCount = 0;

      // Process data rows
      for (let i = dataStartRow; i < rawData.length; i++) {
        const row = rawData[i] || [];

        // Check if row is empty
        if (skipEmptyRows) {
          const isEmptyRow = row.every(
            (cell) => cell === '' || cell === null || cell === undefined
          );
          if (isEmptyRow) {
            skippedCount++;
            continue;
          }
        }

        // Create object from row with normalized headers
        const rowObj = {};
        normalizedHeaders.forEach((header, colIndex) => {
          if (header) {
            let value = row[colIndex] !== undefined ? row[colIndex] : '';

            // Apply field transforms
            if (fieldTransforms[header]) {
              value = fieldTransforms[header](value);
            }

            rowObj[header] = value;
          }
        });

        // Check required fields
        let hasRequiredFields = true;
        for (const field of requiredFields) {
          const value = rowObj[field];
          if (value === null || value === undefined || value === '') {
            sheetErrors.push({
              row: i + 1,
              field,
              message: `${field} is required`,
              type: 'required',
            });
            hasRequiredFields = false;
          }
        }

        // Run custom validation
        if (validateRow && hasRequiredFields) {
          const validationError = validateRow(rowObj, i);
          if (validationError) {
            sheetErrors.push({
              row: i + 1,
              ...validationError,
              type: 'validation',
            });
          }
        }

        processedData.push(rowObj);
      }

      return {
        sheetName,
        data: processedData,
        errors: sheetErrors,
        skipped: skippedCount,
        totalRows: rawData.length - dataStartRow,
      };
    },
    [columnNormalizer, validateRow]
  );

  /**
   * Process an uploaded file through the full pipeline.
   *
   * @param {File} file - The browser File object to process (from `<input type="file">`
   *   or PamGrid's `onUpload` callback).
   * @param {Object} [options] - Processing options
   * @param {string} [options.targetSheet] - Specific sheet name to process. When
   *   provided, only this sheet is processed (others are skipped). Useful for
   *   multi-sheet workbooks where the user selects which sheet to import.
   * @returns {Promise<ProcessingResults>} Full processing results including per-sheet
   *   data, errors, and optional merge results.
   * @throws {Error} If xlsx is not installed or the file cannot be read.
   */
  const processFile = useCallback(
    async (file, options = {}) => {
      const { targetSheet } = options;

      setIsProcessing(true);
      setErrors([]);
      setProgress(0);
      setLastResult(null);

      try {
        updateProgress(10);

        // Import xlsx dynamically
        // Import xlsx - requires xlsx as peer dependency
        let XLSX;
        try {
          XLSX = await import('xlsx');
        } catch {
          throw new Error('xlsx library not available. Please install it: npm install xlsx');
        }

        updateProgress(20);

        // Read file
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });

        updateProgress(30);

        // Process sheets
        const results = {
          fileName: file.name,
          fileType: file.name.toLowerCase().endsWith('.csv') ? 'csv' : 'excel',
          sheets: {},
          allData: [],
          allErrors: [],
          summary: {
            totalSheets: 0,
            totalRows: 0,
            totalErrors: 0,
            processedSheets: [],
          },
        };

        const sheetsToProcess = targetSheet
          ? [targetSheet]
          : workbook.SheetNames;

        const progressPerSheet = 60 / sheetsToProcess.length;

        for (let idx = 0; idx < sheetsToProcess.length; idx++) {
          const sheetName = sheetsToProcess[idx];
          const sheet = workbook.Sheets[sheetName];

          if (!sheet) {
            continue;
          }

          // Get raw data as 2D array
          const rawData = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            defval: '',
            raw: false,
          });

          // Get processor for this sheet
          const processor =
            sheetProcessors[sheetName] || defaultSheetProcessor || {};

          // Process the sheet
          const sheetResult = await processSheet(sheetName, rawData, processor);

          results.sheets[sheetName] = sheetResult;
          results.allData.push(...sheetResult.data);
          results.allErrors.push(
            ...sheetResult.errors.map((err) => ({
              ...err,
              sheet: sheetName,
            }))
          );
          results.summary.totalRows += sheetResult.totalRows || 0;
          results.summary.processedSheets.push(sheetName);

          updateProgress(30 + (idx + 1) * progressPerSheet);
        }

        results.summary.totalSheets = results.summary.processedSheets.length;
        results.summary.totalErrors = results.allErrors.length;

        updateProgress(95);

        // Run merge if mergeStrategy is set and existingRows is provided
        if (mergeStrategy && existingRows) {
          const mergeResult = mergeRows({
            uploadedRows: results.allData,
            existingRows,
            matchField,
            mergeStrategy,
            deduplicateUpload,
            uploadableFields,
            onNewRow,
            onUpdateRow,
          });
          setLastMergeResult(mergeResult);
          results.mergeResult = mergeResult;
        }

        setErrors(results.allErrors);
        setLastResult(results);
        setProgress(100);

        onComplete?.(results);

        return results;
      } catch (error) {
        const errorObj = {
          message: error.message || 'Failed to process file',
          type: 'system',
        };
        setErrors([errorObj]);
        onError?.(error);
        throw error;
      } finally {
        setIsProcessing(false);
      }
    },
    [
      sheetProcessors,
      defaultSheetProcessor,
      processSheet,
      updateProgress,
      onComplete,
      onError,
      existingRows,
      matchField,
      mergeStrategy,
      deduplicateUpload,
      uploadableFields,
      onNewRow,
      onUpdateRow,
    ]
  );

  /**
   * Clear the current processing state. Call this to reset errors, progress,
   * and results (e.g., when the user closes an error dialog or navigates away).
   */
  const reset = useCallback(() => {
    setIsProcessing(false);
    setProgress(0);
    setErrors([]);
    setLastResult(null);
    setLastMergeResult(null);
  }, []);

  return {
    /** Process an uploaded file through the full pipeline */
    processFile,
    /** Whether processing is in progress */
    isProcessing,
    /** Current progress (0-100) */
    progress,
    /** Array of processing errors from the last run */
    errors,
    /** Has any errors from the last run */
    hasErrors: errors.length > 0,
    /** Last processing result (full ProcessingResults object) */
    lastResult,
    /** Last merge result (populated when mergeStrategy is set) */
    lastMergeResult,
    /** Reset processing state (clears errors, progress, results) */
    reset,
  };
};

/**
 * Creates a sheet processor configuration object.
 *
 * This is a convenience factory that provides defaults for all processor options.
 * The returned object is passed as `defaultSheetProcessor` or as a value in the
 * `sheetProcessors` map to `useFileUploadProcessor`.
 *
 * @param {Object} [config={}] - Processor configuration
 * @param {number} [config.headerRow=0] - Row index (0-based) containing column headers.
 *   Set to 1 or higher if your spreadsheet has title/instruction rows above headers.
 * @param {number} [config.dataStartRow=1] - Row index (0-based) where data begins.
 *   Typically `headerRow + 1`.
 * @param {Object<string, string>} [config.columnMappings={}] - Map of normalized
 *   column names to field names. Only used when `columnNormalizer` is not provided
 *   to the parent hook. Prefer `columnNormalizer` for new code.
 * @param {Object<string, Function>} [config.fieldTransforms={}] - Map of field names
 *   to transform functions. Build with `buildFieldTransforms()` from `fieldTransforms.js`.
 * @param {boolean} [config.skipEmptyRows=true] - Skip rows where all cells are empty.
 * @param {Array<string>} [config.requiredFields=[]] - Fields that must be non-empty.
 *   Generates `'required'`-type errors for rows missing these fields.
 * @returns {SheetProcessorConfig} Configuration object ready for use with
 *   `useFileUploadProcessor`.
 *
 * @example <caption>Minimal processor (headers in row 0, data starts row 1)</caption>
 * const processor = createSheetProcessor({
 *   requiredFields: ['CUSIP'],
 * });
 *
 * @example <caption>Full configuration</caption>
 * import { buildFieldTransforms, coerceNumeric } from '@1823-partners/core';
 *
 * const processor = createSheetProcessor({
 *   headerRow: 0,
 *   dataStartRow: 1,
 *   columnMappings: {
 *     'broker': 'BROKER',
 *     'broker name': 'BROKER',
 *     'price file': 'PRICE_FILE',
 *   },
 *   fieldTransforms: {
 *     SECURITY_LIST: normalizeBooleanValue,
 *     ADDITIONS: normalizeBooleanValue,
 *     PRICE: coerceNumeric,
 *   },
 *   skipEmptyRows: true,
 *   requiredFields: ['BROKER', 'PRICE_FILE'],
 * });
 */
export const createSheetProcessor = (config = {}) => {
  const {
    headerRow = 0,
    dataStartRow = 1,
    columnMappings = {},
    fieldTransforms = {},
    skipEmptyRows = true,
    requiredFields = [],
  } = config;

  return {
    headerRow,
    dataStartRow,
    columnMappings,
    fieldTransforms,
    skipEmptyRows,
    requiredFields,
  };
};

/**
 * Creates a stateful uniqueness validator for a single field.
 *
 * Maintains an internal `Set` of seen values. Each time the validator runs,
 * it checks if the value has been seen before. If so, it returns an error.
 * If not, it adds the value to the set.
 *
 * **Important:** Because this validator is stateful (it accumulates seen values),
 * you must create a new instance for each file upload. Do NOT reuse across uploads.
 *
 * @param {string} field - Field name to check for uniqueness (e.g., `'CUSIP'`)
 * @param {Set} [seenValues=new Set()] - Optional pre-populated set of values
 *   to treat as already seen. Useful when checking against existing grid data.
 * @returns {Function} Validator function with signature `(row) => ValidationError | null`.
 *
 * @example
 * import { createUniqueValidator } from '@1823-partners/core';
 *
 * // Check uniqueness within the uploaded file
 * const checkUnique = createUniqueValidator('CUSIP');
 * checkUnique({ CUSIP: '14913UAN0' });  // null (first time)
 * checkUnique({ CUSIP: '14913UAN0' });  // { field: 'CUSIP', message: 'Duplicate CUSIP: 14913UAN0' }
 *
 * @example <caption>Pre-populate with existing CUSIPs</caption>
 * const existingCusips = new Set(gridData.map(r => r.CUSIP));
 * const checkUnique = createUniqueValidator('CUSIP', existingCusips);
 */
export const createUniqueValidator = (field, seenValues = new Set()) => {
  return (row) => {
    const value = row[field];
    if (value && seenValues.has(value)) {
      return {
        field,
        message: `Duplicate ${field}: ${value}`,
      };
    }
    if (value) {
      seenValues.add(value);
    }
    return null;
  };
};

/**
 * Combines multiple validators into a single validator function.
 *
 * Validators are run in order. The first validator that returns an error
 * short-circuits -- later validators are NOT run for that row. This means
 * you should order validators from most fundamental to most specific
 * (e.g., required checks first, then range checks, then pattern checks).
 *
 * @param {...Function} validators - Validator functions, each with signature
 *   `(row, index) => ValidationError | null`.
 * @returns {Function} Combined validator with signature
 *   `(row, index) => ValidationError | null`. Returns `null` if all validators pass.
 *
 * @example
 * import { combineValidators, createRequiredValidator, createRangeValidator } from '@1823-partners/core';
 *
 * const validateRow = combineValidators(
 *   createRequiredValidator(['CUSIP', 'BOOK_YIELD']),
 *   createRangeValidator('BOOK_YIELD', 0, 100),
 * );
 *
 * validateRow({ CUSIP: '', BOOK_YIELD: 50 });   // { field: 'CUSIP', ... } (required fails first)
 * validateRow({ CUSIP: 'X', BOOK_YIELD: 150 }); // { field: 'BOOK_YIELD', ... } (range fails)
 * validateRow({ CUSIP: 'X', BOOK_YIELD: 50 });  // null (all pass)
 */
export const combineValidators = (...validators) => {
  return (row, index) => {
    for (const validator of validators) {
      const error = validator(row, index);
      if (error) {
        return error;
      }
    }
    return null;
  };
};

export default useFileUploadProcessor;
